import { Button } from "@ai-teach/ui-web";
import { useMutation, useQuery } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import {
  SystemConfigDocument,
  UpdateDefaultFsrsSettingsDocument,
} from "../../../generated/graphql";

export function DefaultSettingsEditor() {
  const { user } = useAuth();
  const isSuperuser = user?.role === "super";
  const { data, loading: isLoading, error, refetch } = useQuery(SystemConfigDocument);
  const [updateSettings, { loading: updateLoading }] = useMutation(
    UpdateDefaultFsrsSettingsDocument
  );

  const [formData, setFormData] = useState({
    maxNewCardsPerSession: 0,
    maxReviewsPerSession: 0,
    maxAttemptsPerCard: 0,
    maxSessionDuration: 0,
    minNewCardsPercentage: 0,
    maxCardsPerLesson: 0,
    leechThreshold: 0,
    defaultNotificationTime: "",
    streakRemindersEnabled: false,
  });

  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Initialize form data when data loads
  useEffect(() => {
    if (data?.systemConfig?.defaultFSRSSettings) {
      setFormData({
        maxNewCardsPerSession: data.systemConfig.defaultFSRSSettings.maxNewCardsPerSession,
        maxReviewsPerSession: data.systemConfig.defaultFSRSSettings.maxReviewsPerSession,
        maxAttemptsPerCard: data.systemConfig.defaultFSRSSettings.maxAttemptsPerCard,
        maxSessionDuration: data.systemConfig.defaultFSRSSettings.maxSessionDuration,
        minNewCardsPercentage: data.systemConfig.defaultFSRSSettings.minNewCardsPercentage,
        maxCardsPerLesson: data.systemConfig.defaultFSRSSettings.maxCardsPerLesson,
        leechThreshold: data.systemConfig.defaultFSRSSettings.leechThreshold,
        defaultNotificationTime: data.systemConfig.defaultFSRSSettings.defaultNotificationTime,
        streakRemindersEnabled: data.systemConfig.defaultFSRSSettings.streakRemindersEnabled,
      });
    }
  }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveMessage(null);

    try {
      // Remove __typename from formData before sending to mutation
      const { __typename, ...settingsInput } = formData as typeof formData & {
        __typename?: string;
      };

      await updateSettings({
        variables: {
          settings: settingsInput,
        },
      });
      await refetch();
      setSaveMessage({ type: "success", text: "Default settings updated successfully" });
    } catch (err) {
      setSaveMessage({
        type: "error",
        text: err instanceof Error ? err.message : "Failed to update settings",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-700 dark:text-gray-500">Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-red-500">Error loading settings: {error.message}</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="panel p-6">
      <div className="mb-6">
        <h3 className="h5">Default FSRS Settings</h3>
        {!isSuperuser && (
          <p className="mt-2 text-sm text-warning-500 font-medium">
            ⚠️ Super user access required to modify these settings
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block label mb-2">Max New Cards Per Session</label>
          <input
            type="number"
            value={formData.maxNewCardsPerSession}
            onChange={(e) =>
              setFormData({ ...formData, maxNewCardsPerSession: Number.parseInt(e.target.value) })
            }
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Reviews Per Session</label>
          <input
            type="number"
            value={formData.maxReviewsPerSession}
            onChange={(e) =>
              setFormData({ ...formData, maxReviewsPerSession: Number.parseInt(e.target.value) })
            }
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Attempts Per Card</label>
          <input
            type="number"
            value={formData.maxAttemptsPerCard}
            onChange={(e) =>
              setFormData({ ...formData, maxAttemptsPerCard: Number.parseInt(e.target.value) })
            }
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Session Duration (minutes)</label>
          <input
            type="number"
            value={formData.maxSessionDuration}
            onChange={(e) =>
              setFormData({ ...formData, maxSessionDuration: Number.parseInt(e.target.value) })
            }
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Min New Cards Percentage</label>
          <input
            type="number"
            value={formData.minNewCardsPercentage}
            onChange={(e) =>
              setFormData({ ...formData, minNewCardsPercentage: Number.parseInt(e.target.value) })
            }
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            min="0"
            max="100"
          />
        </div>

        <div>
          <label className="block label mb-2">Max Cards Per Lesson</label>
          <input
            type="number"
            value={formData.maxCardsPerLesson}
            onChange={(e) =>
              setFormData({ ...formData, maxCardsPerLesson: Number.parseInt(e.target.value) })
            }
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Leech Threshold</label>
          <input
            type="number"
            value={formData.leechThreshold}
            onChange={(e) =>
              setFormData({ ...formData, leechThreshold: Number.parseInt(e.target.value) })
            }
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            min="0"
          />
        </div>

        <div>
          <label className="block label mb-2">Default Notification Time</label>
          <input
            type="time"
            value={formData.defaultNotificationTime}
            onChange={(e) => setFormData({ ...formData, defaultNotificationTime: e.target.value })}
            disabled={!isSuperuser}
            className="w-full px-3 py-2 bg-surface border border-gray-200 dark:border-gray-800 rounded-md text-secondary-200 placeholder:text-gray-700 dark:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50 [color-scheme:dark] picker-teal"
          />
        </div>

        <div className="md:col-span-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.streakRemindersEnabled}
              onChange={(e) =>
                setFormData({ ...formData, streakRemindersEnabled: e.target.checked })
              }
              disabled={!isSuperuser}
              className="w-4 h-4 text-primary-600 bg-surface border-gray-200 dark:border-gray-800 rounded focus:ring-primary-500 disabled:bg-gray-100 dark:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="ml-2 text-sm text-secondary-200">Enable Streak Reminders</span>
          </label>
        </div>
      </div>

      {saveMessage && (
        <div
          className={`mt-6 p-4 rounded-md border ${
            saveMessage.type === "success"
              ? "bg-success-500/10 border-success-500/20 text-success-500"
              : "bg-error-500/10 border-error-500/20 text-error-500"
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button type="submit" variant="primary" disabled={updateLoading || !isSuperuser}>
          {updateLoading ? "Saving..." : "Save Default Settings"}
        </Button>
      </div>
    </form>
  );
}
